/**
 * Activity streak calculation.
 *
 * Pure, dependency-free module: no React, no gRPC/API calls. Takes the
 * dates of a user's activities (already fetched elsewhere, e.g. Dashboard's
 * existing `getActivities` call) and derives streak stats from them.
 *
 * TIME ZONE DECISION: days are bucketed in the VIEWER'S LOCAL time, not
 * UTC. A streak is a personal habit measure with exactly one viewer (its
 * owner) - "did I train today" has to match the calendar day the user
 * actually experienced, not a server-side UTC day that may already be
 * tomorrow, or still be yesterday, from their seat. This is a deliberate
 * departure from the dowtex stats feature, which buckets in UTC so that
 * every viewer of *shared* data agrees on "what day" something happened;
 * streaks have no shared audience to agree with, so local time serves the
 * user better. The local day key is derived from getFullYear/getMonth/
 * getDate - never from an ISO-string slice, which would silently reintroduce
 * UTC bucketing.
 */

export type Streaks = {
    current: number;
    longest: number;
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Encodes a Date's local calendar day as a sortable/comparable integer, e.g. 20260827. */
function toLocalDayKey(date: Date): number {
    return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

/**
 * Converts a YYYYMMDD day key back to a fixed UTC-midnight timestamp, purely
 * as a way to measure "exactly one calendar day apart" via a constant
 * 86_400_000ms step. The actual local wall-clock time is irrelevant once
 * we're diffing keys, and going through UTC here sidesteps any local-DST
 * arithmetic bugs when comparing two arbitrary calendar days.
 */
function dayKeyToComparableTimestamp(key: number): number {
    const year = Math.floor(key / 10000);
    const month = Math.floor((key % 10000) / 100) - 1;
    const day = key % 100;
    return Date.UTC(year, month, day);
}

/**
 * A gap of one or two rest days between active days does not break a
 * streak; a gap of three or more rest days does. Expressed as calendar-day
 * difference between consecutive active days: gap === 1 means trained again
 * the next day (no rest day), gap === 2 or 3 means one or two rest days in
 * between - both still continue the streak. gap >= 4 (three or more rest
 * days) breaks it.
 *
 * CONSEQUENCE (intended, not a bug): training exactly every third day keeps
 * a streak alive indefinitely, since every gap is exactly 3. Rest days are
 * being deliberately rewarded by this rule, so that's the trade the owner
 * chose - documented here so nobody "fixes" it later.
 */
const MAX_CONTINUING_GAP_DAYS = 3;

/**
 * Computes the current and longest activity streaks from a list of activity
 * dates (typically each activity's `tsinsert`, one entry per activity - no
 * need to pre-deduplicate).
 *
 * - A streak continues across a gap of up to MAX_CONTINUING_GAP_DAYS
 *   calendar days between one active day and the next (see the constant's
 *   doc comment for the rest-day rule and its consequence). A larger gap
 *   breaks it.
 * - Streak length counts the number of active days in the run, not the
 *   number of calendar days it spans - "12 days trained" is what's shown,
 *   even though the run may cover more calendar days once rest days are
 *   folded in.
 * - "Current" streak = the run ending at the most recent active day, but
 *   only if that day is today or within MAX_CONTINUING_GAP_DAYS days ago;
 *   otherwise the streak is considered broken and current is 0.
 * - "Longest" streak = the longest such run anywhere in the history,
 *   regardless of whether it's still active.
 * - Multiple activities on the same local day count once.
 * - Empty input yields { current: 0, longest: 0 }.
 *
 * @param now Reference "now" instant, injectable for deterministic tests.
 */
export function computeStreaks(dates: Date[], now: Date = new Date()): Streaks {
    if (dates.length === 0) {
        return { current: 0, longest: 0 };
    }

    const uniqueDayKeys = Array.from(new Set(dates.map(toLocalDayKey))).sort((a, b) => a - b);

    let longest = 1;
    let run = 1;
    for (let i = 1; i < uniqueDayKeys.length; i++) {
        const gapDays =
            (dayKeyToComparableTimestamp(uniqueDayKeys[i]) - dayKeyToComparableTimestamp(uniqueDayKeys[i - 1])) /
            ONE_DAY_MS;
        run = gapDays <= MAX_CONTINUING_GAP_DAYS ? run + 1 : 1;
        if (run > longest) longest = run;
    }

    const todayKey = toLocalDayKey(now);
    const lastKey = uniqueDayKeys.at(-1)!;
    const daysSinceLast = (dayKeyToComparableTimestamp(todayKey) - dayKeyToComparableTimestamp(lastKey)) / ONE_DAY_MS;

    let current = 0;
    if (daysSinceLast <= MAX_CONTINUING_GAP_DAYS) {
        current = 1;
        for (let i = uniqueDayKeys.length - 1; i > 0; i--) {
            const gapDays =
                (dayKeyToComparableTimestamp(uniqueDayKeys[i]) - dayKeyToComparableTimestamp(uniqueDayKeys[i - 1])) /
                ONE_DAY_MS;
            if (gapDays <= MAX_CONTINUING_GAP_DAYS) {
                current++;
            } else {
                break;
            }
        }
    }

    return { current, longest };
}
